// client/pages/Login.jsx
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../constants/api";
import { useAuth } from "../components/ui/AuthContext.jsx";
import { showSuccess, showError, showInfo, promise, extractErrorMessage } from "../utils/toast.js";
import { Eye, EyeOff, Loader2, AlertCircle, Mail, Lock, ShieldCheck, Fingerprint } from "lucide-react";
import { loginWithPasskey, isPasskeyAvailable } from "../constants/passkeys";
import { useTheme } from "../context/ThemeContext.jsx";
import AppButton from "../components/ui/AppButton.jsx";
import { Input } from "../components/forms/shared/FormAtoms.jsx";
import {
  AuthBrandingHeader,
  AuthCardShell,
  AuthFooterMetaLine,
  AuthLoginBackground,
  AuthLoginBrandPill,
  authLoginInputClassName,
  authFieldErrorClassName,
  authLabelClassName,
  AUTH_PAGE_GRADIENT,
} from "../components/Login/AuthVisualShell.jsx";

/* ===== Validierung ===== */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function validateIdentifier(v) {
  const val = (v || "").trim();
  if (!val) return "Bitte Name oder E-Mail eingeben.";
  if (val.includes("@") && !EMAIL_RE.test(val)) return "Bitte gültige E-Mail-Adresse eingeben.";
  return "";
}
function validatePasswort(v) {
  if (!(v || "").trim()) return "Bitte Passwort eingeben.";
  return "";
}

/* ===== Fehler-Nachrichten nach Fehlertyp ===== */
function classifyError(err) {
  const status = err?.response?.status ?? err?.status;
  const serverMsg = err?.response?.data?.msg ?? err?.data?.msg;
  if (status === 401) return serverMsg || "E-Mail oder Passwort stimmt nicht.";
  if (status === 429) return serverMsg || "Zu viele Fehlversuche — bitte kurz warten.";
  if (status === 403) return serverMsg || "Zugriff verweigert.";
  if (status >= 500) return "Serverfehler — bitte später erneut versuchen.";
  return serverMsg || extractErrorMessage(err, "srv.error");
}
export default function Login() {
    const {
    refreshAuth
  } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [passwort, setPasswort] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [identifierErr, setIdentifierErr] = useState("");
  const [passwortErr, setPasswortErr] = useState("");
  const [formErr, setFormErr] = useState("");
  const [touched, setTouched] = useState({
    identifier: false,
    passwort: false
  });
  const [needs2FA, setNeeds2FA] = useState(false);
  const [twofaCode, setTwofaCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(true);
  const [loading2FA, setLoading2FA] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  const identifierRef = useRef(null);
  const passwortRef = useRef(null);
  const submitRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    document.title = "Login • WireTrack";
    (async () => {
      const available = await isPasskeyAvailable();
      if (mountedRef.current) setPasskeyAvailable(available);
    })();
    try {
      sessionStorage.removeItem("user");
    } catch {}
    identifierRef.current?.focus();
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (touched.identifier) setIdentifierErr(validateIdentifier(identifier));
    if (formErr) setFormErr("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier]);
  useEffect(() => {
    if (touched.passwort) setPasswortErr(validatePasswort(passwort));
    if (formErr) setFormErr("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwort]);
  const handleBlur = useCallback(field => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
    if (field === "identifier") setIdentifierErr(validateIdentifier(identifier));
    if (field === "passwort") setPasswortErr(validatePasswort(passwort));
  }, [identifier, passwort]);
  const canSubmit = useMemo(() => {
    const idOk = identifier.trim() && !validateIdentifier(identifier);
    const pwOk = passwort.trim();
    return idOk && pwOk && !loading;
  }, [identifier, passwort, loading]);
  const focusFirstError = useCallback(() => {
    if (validateIdentifier(identifier)) identifierRef.current?.focus();else if (validatePasswort(passwort)) passwortRef.current?.focus();
  }, [identifier, passwort]);

  /* ===== Login ===== */
  const handleLogin = useCallback(async e => {
    e.preventDefault();
    if (loading) return;
    setTouched({
      identifier: true,
      passwort: true
    });
    const idErr = validateIdentifier(identifier);
    const pwErr = validatePasswort(passwort);
    setIdentifierErr(idErr);
    setPasswortErr(pwErr);
    if (idErr || pwErr) {
      focusFirstError();
      return;
    }
    if (mountedRef.current) {
      setFormErr("");
      setLoading(true);
    }
    const id = identifier.trim();
    const pw = passwort.trim();
    try {
      const res = await promise(api.post("/login", {
        identifier: id,
        passwort: pw,
        remember: !!remember
      }), {
        loading: "Anmeldung…",
        success: r => r?.data?.step === "verify_2fa" ? "Bitte 2FA-Code eingeben." : "Willkommen zurück!",
        error: "auth.invalid"
      });
      const data = res.data || {};
      if (!data.erfolg) {
        if (mountedRef.current) setFormErr(data?.msg || "Anmeldung fehlgeschlagen.");
        if (mountedRef.current) setPasswort("");
        passwortRef.current?.focus();
        return;
      }
      switch (data.step) {
        case "verify_2fa":
          setNeeds2FA(true);
          setTwofaCode("");
          break;
        case "setup_2fa":
          showInfo("Bitte 2FA einrichten.");
          navigate("/2fa/setup");
          break;
        case "change_password":
          navigate("/first-password");
          break;
        case "academy":
          sessionStorage.setItem("user", JSON.stringify(data.user || null));
          try {
            await refreshAuth(true);
          } catch {}
          navigate("/");
          break;
        default:
          sessionStorage.setItem("user", JSON.stringify(data.user || null));
          try {
            await refreshAuth(true);
          } catch {}
          navigate("/");
      }
    } catch (err) {
      if (mountedRef.current) setFormErr(classifyError(err));
      if (mountedRef.current) setPasswort("");
      passwortRef.current?.focus();
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [identifier, passwort, remember, loading, navigate, focusFirstError, refreshAuth]);

  /* ===== Passkey ===== */
  const handlePasskeyLogin = useCallback(async () => {
    if (!passkeyAvailable || passkeyBusy) return;
    if (mountedRef.current) setPasskeyBusy(true);
    try {
      const res = await loginWithPasskey(identifier.trim().toLowerCase() || undefined);
      if (res?.erfolg) {
        if (res?.needChangePassword) return navigate("/first-password");
        sessionStorage.setItem("user", JSON.stringify(res.user || null));
        try {
          await refreshAuth(true);
        } catch {}
        showSuccess("Willkommen zurück!");
        navigate("/");
      } else {
        showError(res?.msg || "Passkey-Login fehlgeschlagen.");
      }
    } catch (err) {
      showError(err, {
        fallbackKey: "Passkey-Login abgebrochen."
      });
    } finally {
      if (mountedRef.current) setPasskeyBusy(false);
    }
  }, [identifier, passkeyAvailable, passkeyBusy, navigate, refreshAuth]);

  /* ===== 2FA ===== */
  const handleVerify2FA = useCallback(async e => {
    e?.preventDefault?.();
    if (loading2FA) return;
    const code = twofaCode.trim();
    if (!code || code.length < 6) return showError("Bitte gültigen 6-stelligen Code eingeben.");
    if (mountedRef.current) setLoading2FA(true);
    try {
      const res = await promise(api.post("/2fa/verify", {
        token: code,
        rememberDevice: !!rememberDevice
      }), {
        loading: "Prüfe 2FA…",
        success: "Willkommen zurück!",
        error: "srv.error"
      });
      const {
        data
      } = res;
      if (!data?.erfolg) return showError(data?.msg || "Code ungültig.");
      if (mountedRef.current) {
        setNeeds2FA(false);
        setTwofaCode("");
      }
      if (data?.needChangePassword) return navigate("/first-password");
      sessionStorage.setItem("user", JSON.stringify(data.user || null));
      try {
        await refreshAuth(true);
      } catch {}
      navigate("/");
    } catch (_e2) {} finally {
      if (mountedRef.current) setLoading2FA(false);
    }
  }, [twofaCode, rememberDevice, loading2FA, navigate, refreshAuth]);
  function handlePwKey(e) {
    try {
      setCapsLock(Boolean(e.getModifierState?.("CapsLock")));
    } catch {
      setCapsLock(false);
    }
  }
  /* ===== Klassen (DRY — siehe AuthVisualShell) ===== */
  const inputBase = `${authLoginInputClassName}`;
  const inputError = authFieldErrorClassName;
  const labelCls = authLabelClassName;
  return <div className={`login-page min-h-screen w-full relative overflow-hidden ${AUTH_PAGE_GRADIENT}`}>
      <AuthLoginBackground isDark={isDark} rich />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-3 pb-6 pt-4 sm:px-4 sm:py-12">
        <div className="w-full max-w-full sm:max-w-[min(32rem,calc(100vw-1.5rem))]">
          <AuthCardShell isDark={isDark} premium contentClassName="space-y-3.5 sm:space-y-6">
            <div className="login-hero-reveal login-hero-reveal-d1">
              <AuthBrandingHeader variant="premium" />
            </div>

            <div className="text-center pt-0.5 login-hero-reveal login-hero-reveal-d2 sm:pt-1">
              <h1 className="text-[1.35rem] font-semibold tracking-tight text-balance leading-tight bg-gradient-to-br from-white via-white to-white/55 bg-clip-text text-transparent sm:text-[1.85rem]">
                Willkommen zurück
              </h1>
            </div>

            {passkeyAvailable ? (
              <div className="login-hero-reveal login-hero-reveal-d3">
                <AppButton
                  type="button"
                  variant="secondary"
                  size="lg"
                  disabled={passkeyBusy}
                  onClick={handlePasskeyLogin}
                  className="login-passkey-btn w-full justify-center gap-2.5 font-semibold max-sm:min-h-[46px] max-sm:text-[14px] min-h-[48px] text-[15px] rounded-xl border-white/18"
                >
                  {passkeyBusy ? <Loader2 className="w-5 h-5 animate-spin shrink-0" /> : <Fingerprint className="login-passkey-icon w-5 h-5 shrink-0 opacity-90" />}
                  {passkeyBusy ? "Bitte bestätigen…" : "Mit Passkey anmelden"}
                </AppButton>
              </div>
            ) : (
              <p className="text-center text-[12px] leading-snug text-white/38 px-1 login-hero-reveal login-hero-reveal-d3">
                Passkey nicht verfügbar — nutze Name oder E-Mail und Passwort.
              </p>
            )}

            <div className="flex items-center gap-3 py-0.5 login-hero-reveal login-hero-reveal-d4">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/12 to-white/12" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/32">oder</span>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent via-white/12 to-white/12" />
            </div>

            <form onSubmit={handleLogin} noValidate className="space-y-3 sm:space-y-[1.125rem] login-hero-reveal login-hero-reveal-d5">
              {formErr && <div role="alert" aria-live="assertive" className={`login-shake flex items-start gap-2 text-sm font-medium px-3 py-2.5 rounded-xl ${"text-red-300 bg-red-500/10 border border-red-500/20"}`}>
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{formErr}</span>
                </div>}

              {/* Identifier */}
              <div>
                <label htmlFor="login-identifier" className={labelCls}>Name oder E-Mail</label>
                <div className="relative group">
                  <span
                    className="pointer-events-none absolute left-3.5 top-1/2 flex h-[22px] w-[22px] -translate-y-1/2 items-center justify-center"
                    aria-hidden
                  >
                    <Mail
                      className={`login-field-icon login-field-icon--mail h-[18px] w-[18px] shrink-0 text-white/25 transition-colors group-focus-within:text-blue-500 ${touched.identifier && identifierErr ? "!text-red-400" : ""}`}
                    />
                  </span>
                  <Input ref={identifierRef} id="login-identifier" type="text" value={identifier} onChange={e => setIdentifier(e.target.value)} onBlur={() => handleBlur("identifier")} className={`${inputBase} ${touched.identifier && identifierErr ? inputError : ""}`} autoComplete="username" enterKeyHint="next" aria-required="true" aria-invalid={touched.identifier && !!identifierErr} aria-describedby={touched.identifier && identifierErr ? "login-id-err" : undefined} disabled={loading} placeholder="max.muster@firma.ch" />
                </div>
                {touched.identifier && identifierErr && <p id="login-id-err" role="alert" className={`text-xs mt-1.5 ml-0.5 flex items-center gap-1.5 ${"text-red-400"}`}>
                    <AlertCircle className="w-3 h-3 shrink-0" />{identifierErr}
                  </p>}
              </div>

              {/* Passwort */}
              <div>
                <label htmlFor="login-passwort" className={labelCls}>Passwort</label>
                <div className="relative group">
                  <span
                    className="pointer-events-none absolute left-3.5 top-1/2 flex h-[22px] w-[22px] -translate-y-1/2 items-center justify-center"
                    aria-hidden
                  >
                    <Lock
                      className={`login-field-icon login-field-icon--lock h-[18px] w-[18px] shrink-0 text-white/25 transition-colors group-focus-within:text-blue-500 ${touched.passwort && passwortErr ? "!text-red-400" : ""}`}
                    />
                  </span>
                  <Input ref={passwortRef} id="login-passwort" type={showPw ? "text" : "password"} value={passwort} onChange={e => setPasswort(e.target.value)} onBlur={() => handleBlur("passwort")} onKeyDown={handlePwKey} onKeyUp={handlePwKey} className={`${inputBase} !pr-12 ${touched.passwort && passwortErr ? inputError : ""}`} autoComplete="current-password" enterKeyHint="go" aria-required="true" aria-invalid={touched.passwort && !!passwortErr} aria-describedby={[touched.passwort && passwortErr ? "login-pw-err" : null, capsLock ? "login-caps" : null].filter(Boolean).join(" ") || undefined} disabled={loading} />
                  <AppButton type="button" variant="ghost" tabIndex={-1} onClick={() => setShowPw(v => !v)} className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-2 rounded-lg min-h-0 h-auto w-auto border-0 shadow-none ${"!text-white/30 hover:!text-white/70 hover:!bg-white/5"}`} aria-pressed={showPw} aria-label={showPw ? "Passwort verbergen" : "Passwort anzeigen"}>
                    {showPw ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                  </AppButton>
                </div>
                {touched.passwort && passwortErr && <p id="login-pw-err" role="alert" className={`text-xs mt-1.5 ml-0.5 flex items-center gap-1.5 ${"text-red-400"}`}>
                    <AlertCircle className="w-3 h-3 shrink-0" />{passwortErr}
                  </p>}
              </div>

              {capsLock && <div id="login-caps" role="status" className={`text-xs font-medium px-3 py-2 rounded-xl flex items-center gap-2 ${"text-amber-300/80 bg-amber-500/10 border border-amber-500/20"}`}>
                  <span className="text-base leading-none">⇪</span> Feststelltaste ist aktiv
                </div>}

              {/* Options row */}
              <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
                <label className={`flex items-center gap-2 text-[13px] cursor-pointer select-none max-sm:justify-center ${"text-white/60"}`}>
                  <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} className={`w-[15px] h-[15px] rounded ${"accent-blue-500"}`} />
                  Angemeldet bleiben
                </label>
                <a
                  href="/reset"
                  className={`text-[13px] font-medium hover:underline transition-colors max-sm:text-center sm:text-right ${"text-blue-400/70 hover:text-blue-300"}`}
                >
                  Passwort vergessen?
                </a>
              </div>

              {/* Submit */}
              <AppButton
                ref={submitRef}
                type="submit"
                disabled={!canSubmit}
                variant="primary"
                size="lg"
                className={`login-submit-premium relative overflow-hidden w-full justify-center gap-2.5 font-semibold tracking-tight max-sm:min-h-[50px] max-sm:text-[15px] min-h-[52px] text-[15px] sm:min-h-[54px] sm:text-base rounded-[1rem] disabled:!opacity-100 ${!canSubmit ? "cursor-not-allowed" : ""}`}
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin shrink-0" /> : null}
                {loading ? "Anmelden…" : "Einloggen"}
              </AppButton>
            </form>

            <div className="flex flex-col gap-2 pt-0.5 login-hero-reveal login-hero-reveal-d6 sm:gap-3 sm:pt-1">
              <AuthLoginBrandPill />
              <AuthFooterMetaLine />
            </div>
          </AuthCardShell>
        </div>
      </div>

      {needs2FA && <TwoFAModal onClose={() => setNeeds2FA(false)} code={twofaCode} setCode={setTwofaCode} rememberDevice={rememberDevice} setRememberDevice={setRememberDevice} onVerify={handleVerify2FA} loading={loading2FA} />}
    </div>;
}

/* ===== 2FA-Modal ===== */
function TwoFAModal({
  onClose,
  code,
  setCode,
  rememberDevice,
  setRememberDevice,
  onVerify,
  loading
}) {
    const inputRef = useRef(null);
  const [show, setShow] = useState(false);
  const mountRafRef = useRef(null);
  useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    mountRafRef.current = requestAnimationFrame(() => setShow(true));
    inputRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      if (mountRafRef.current) cancelAnimationFrame(mountRafRef.current);
    };
  }, [onClose]);
  return <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${show ? "opacity-100" : "opacity-0"} bg-black/70 backdrop-blur-sm`} role="dialog" aria-modal="true" aria-label="Zwei-Faktor-Authentifizierung">
      <div
        className={`w-full max-w-sm rounded-2xl border border-white/[0.1] bg-[#0b1020]/95 p-6 shadow-xl transition-all duration-300 ${show ? "scale-100 translate-y-0" : "scale-[0.98] translate-y-2"}`}
      >
        <div className="text-center mb-5 space-y-1.5">
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-blue-500/10 border border-white/10 mb-0.5">
            <ShieldCheck className="w-5 h-5 text-blue-400" />
          </div>
          <h2 className="text-base font-semibold text-white">Zwei-Faktor bestätigen</h2>
          <p className="text-xs text-white/45 leading-snug">6-stelliger Code aus der Authenticator-App.</p>
        </div>
        <form onSubmit={onVerify} className="space-y-3">
          <div>
            <label htmlFor="twofa-code" className="sr-only">6-stelliger Code</label>
            <Input ref={inputRef} id="twofa-code" value={code} onChange={e => setCode(e.target.value.replace(/\s|-/g, ""))} className={`w-full rounded-xl px-3 py-3 outline-none tracking-[0.28em] text-center text-lg font-mono transition-all ${"bg-white/[0.04] border border-white/[0.1] text-white focus:bg-white/[0.06] focus:border-blue-500/45 focus:ring-1 focus:ring-blue-500/25"}`} placeholder="••••••" inputMode="numeric" autoComplete="one-time-code" enterKeyHint="done" pattern="\d*" maxLength={6} aria-required="true" disabled={loading} />
          </div>
          <label className={`flex items-center gap-2 text-xs cursor-pointer select-none ${"text-white/45"}`}>
            <input type="checkbox" checked={rememberDevice} onChange={e => setRememberDevice(e.target.checked)} className={`w-[14px] h-[14px] rounded ${"accent-blue-500"}`} />
            Gerät 14 Tage merken
          </label>
          <div className="flex items-center gap-2">
            <AppButton type="submit" variant="primary" size="md" disabled={loading || !code || code.length < 6} className="flex-1 justify-center gap-2 font-semibold disabled:opacity-40">
              {loading && <Loader2 className="w-4 h-4 animate-spin shrink-0" />}
              {loading ? "Prüfe…" : "Bestätigen"}
            </AppButton>
            <AppButton type="button" variant="secondary" size="md" onClick={onClose} disabled={loading} className="shrink-0 px-4">
              Zurück
            </AppButton>
          </div>
        </form>
      </div>
    </div>;
}